import { landing } from "./landingTokens";
// Moss and Clearing (the two production variants) use self-hosted fonts instead of a `fontsHref`: the
// stylesheet ships with the page, so no round trip to Google Fonts delays the first paint.
import "../../fonts/marketing-fonts/marketing-fonts.css";

// Each landing-page version is a theme: the page content and sections are shared,
// and a theme supplies the palette, typography, shapes and a few layout choices.
export type HeroLayout = "framer" | "fieldGuide" | "nightLibrary" | "sticker" | "gallery" | "blueprint" | "moss" | "clearing" | "canopy" | "riverbank" | "diorama";

export type LandingTheme = {
  id: string;
  name: string;
  // One line shown in the version switcher so reviewers know the intent.
  direction: string;
  // Google Fonts stylesheet for this theme (undefined = fonts already loaded by the app).
  fontsHref?: string;
  isDark: boolean;
  // The nav sits on a dark surface (light logo and links) even if the page is light.
  navOnDark: boolean;
  heroLayout: HeroLayout;
  // Alignment of section headings/body below the hero. One rule per page.
  sectionAlign: "left" | "center";
  // Blueprint-style "Fig. N — …" labels on every section.
  useFigureLabels: boolean;
  // Use the original page's section layouts and copy below the hero (the Moss family) instead of
  // the shared themed sections.
  usesOriginalSectionLayouts?: boolean;
  font: {
    display: string;
    body: string;
    mono: string;
  };
  heading: {
    weight: number;
    letterSpacing: string;
    // Display faces differ in visual size; section headings are scaled by this factor.
    sizeScale: number;
    transform: "none" | "lowercase" | "uppercase";
  };
  color: {
    pageBg: string;
    surface: string;
    surfaceAlt: string;
    heading: string;
    text: string;
    muted: string;
    accent: string;
    accentHover: string;
    accentDark: string;
    accentSoft: string;
    highlight: string;
    // The 'correct answer' state color — the one place a theme's second accent may appear.
    success: string;
    border: string;
    inputBg: string;
    inputText: string;
    footerBg: string;
    footerText: string;
    footerLink: string;
  };
  shape: {
    cardRadius: string;
    buttonRadius: string;
  };
  card: {
    border: string;
    shadow: string;
  };
  button: {
    primaryColor: string;
    shadow: string;
    border: string;
    hoverTransform: string;
  };
  chart: {
    jungle: string;
    competitors: [string, string, string, string];
    gridline: string;
    baseline: string;
    trackBg: string;
  };
  // Optional repeating background layer drawn over the page background (e.g. grid paper).
  pageBackgroundLayer?: {
    backgroundImage: string;
    backgroundSize: string;
  };
  footer: {
    // Closes the page with the mascot illustration (with the final call to action over its sky).
    showIllustration: boolean;
  };
};

// Neutral competitor greys with real lightness steps so the Jungle bar is the only color in the chart.
const LIGHT_THEME_COMPETITOR_GREYS: [string, string, string, string] = ["#8A9490", "#A9B1AE", "#C6CCC9", "#DEE2E0"];
const DARK_THEME_COMPETITOR_GREYS: [string, string, string, string] = ["#6E7C74", "#59665E", "#465149", "#343D37"];

const ORIGINAL_THEME: LandingTheme = {
  id: "original",
  name: "Original",
  direction: "Pixel match of the current Framer page",
  isDark: false,
  navOnDark: false,
  heroLayout: "framer",
  sectionAlign: "center",
  useFigureLabels: false,
  font: { display: landing.font.heading, body: landing.font.body, mono: "Outfit, sans-serif" },
  heading: { weight: 400, letterSpacing: "0", sizeScale: 1, transform: "none" },
  color: {
    pageBg: landing.color.pageBg,
    surface: "#FFFFFF",
    surfaceAlt: landing.color.cardGreen,
    heading: landing.color.heading,
    text: landing.color.text,
    muted: landing.color.muted,
    accent: landing.color.green,
    accentHover: landing.color.greenHover,
    accentDark: landing.color.greenDark,
    accentSoft: landing.color.inputBg,
    highlight: landing.color.greenChart,
    success: "#003D16",
    border: landing.color.cardBorder,
    inputBg: landing.color.inputBg,
    inputText: landing.color.body,
    footerBg: landing.color.footerBg,
    footerText: "rgba(255, 255, 255, 0.9)",
    footerLink: landing.color.footerLink,
  },
  shape: { cardRadius: "20px", buttonRadius: "12px" },
  card: { border: `1px solid ${landing.color.cardBorder}`, shadow: "none" },
  button: { primaryColor: "#FFFFFF", shadow: "none", border: "none", hoverTransform: "rotate(-3deg)" },
  chart: {
    jungle: landing.color.greenChart,
    competitors: ["#8E96A3", "#A6ADB7", "#BEC4CC", "#D4D8DE"],
    gridline: "#E5E9ED",
    baseline: "#C5CDD3",
    trackBg: "#F4F6F8",
  },
  footer: { showIllustration: true },
};

// Seed read: quiet lowercase opening, doubled letters, digits circling 3-4-5.
// A studied, two-column field guide; the doubled letters become highlighter strokes.
const FIELD_GUIDE_THEME: LandingTheme = {
  id: "field-guide",
  name: "Field guide",
  direction: "The botanist's notebook — paper, taped specimens, hand-written notes, plates under a lens",
  fontsHref:
    "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Instrument+Sans:wght@400;500;600&family=Caveat:wght@500;600&display=swap",
  isDark: false,
  navOnDark: false,
  heroLayout: "fieldGuide",
  sectionAlign: "left",
  useFigureLabels: false,
  font: { display: '"Fraunces", Georgia, serif', body: '"Instrument Sans", sans-serif', mono: '"Instrument Sans", sans-serif' },
  heading: { weight: 500, letterSpacing: "-0.015em", sizeScale: 1.08, transform: "none" },
  color: {
    pageBg: "#F2F7EF",
    surface: "#FFFFFF",
    surfaceAlt: "#E8F1E6",
    heading: "#12301F",
    text: "#27392E",
    muted: "#6B7C70",
    accent: "#1F7A46",
    accentHover: "#155C34",
    accentDark: "#0F3D25",
    accentSoft: "#DDF0DF",
    highlight: "#E6F56B",
    success: "#0F3D25",
    border: "#CFE0D2",
    inputBg: "#EEF5EC",
    inputText: "#27392E",
    footerBg: "#12301F",
    footerText: "#E7F0E8",
    footerLink: "#A9C7B0",
  },
  shape: { cardRadius: "12px", buttonRadius: "10px" },
  card: { border: "1px solid #CFE0D2", shadow: "none" },
  button: { primaryColor: "#FFFFFF", shadow: "none", border: "none", hoverTransform: "translateY(-2px)" },
  chart: {
    jungle: "#1F7A46",
    competitors: LIGHT_THEME_COMPETITOR_GREYS,
    gridline: "#DCE8DD",
    baseline: "#9FB5A4",
    trackBg: "#EEF5EC",
  },
  footer: { showIllustration: false },
};

// Seed read: forked y's, hissing consonants, the pair "91" twice, ends on a zero.
// Late and nocturnal: bottle green, one warm lamp, an italic serif.
const NIGHT_LIBRARY_THEME: LandingTheme = {
  id: "night-library",
  name: "Night library",
  direction: "Late-night study — bottle green, brass for the one action, a quiet serif",
  fontsHref:
    "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Manrope:wght@400;500;600;700&display=swap",
  isDark: true,
  navOnDark: true,
  heroLayout: "nightLibrary",
  sectionAlign: "center",
  useFigureLabels: false,
  font: { display: '"Instrument Serif", Georgia, serif', body: '"Manrope", sans-serif', mono: '"Manrope", sans-serif' },
  heading: { weight: 400, letterSpacing: "-0.02em", sizeScale: 1.2, transform: "none" },
  color: {
    pageBg: "#0E1F17",
    surface: "#142A1F",
    surfaceAlt: "#1B3427",
    heading: "#F3EEE2",
    text: "#D5DACF",
    muted: "#8FA093",
    accent: "#E4B15A",
    accentHover: "#F0C36F",
    accentDark: "#3FA36B",
    accentSoft: "rgba(228, 177, 90, 0.22)",
    highlight: "#E4B15A",
    success: "#E4B15A",
    border: "rgba(255, 255, 255, 0.08)",
    inputBg: "#0B1A12",
    inputText: "#D5DACF",
    footerBg: "#081510",
    footerText: "#E7E9E2",
    footerLink: "#9FB3A4",
  },
  shape: { cardRadius: "16px", buttonRadius: "999px" },
  card: { border: "1px solid rgba(255, 255, 255, 0.1)", shadow: "none" },
  button: { primaryColor: "#0E1F17", shadow: "none", border: "none", hoverTransform: "translateY(-1px)" },
  chart: {
    jungle: "#E4B15A",
    competitors: DARK_THEME_COMPETITOR_GREYS,
    gridline: "rgba(255, 255, 255, 0.06)",
    baseline: "rgba(255, 255, 255, 0.2)",
    trackBg: "#0B1A12",
  },
  footer: { showIllustration: false },
};

// Seed read: loud runs of capitals, a 4-3-2-1 countdown in the digits, a bouncing tail.
// Energy and speed: heavy grotesk, hard offset shadows, stickers on a sheet.
const STICKER_THEME: LandingTheme = {
  id: "sticker-sheet",
  name: "Sticker sheet",
  direction: "Loud and fast — heavy grotesk, black outlines, hard shadows, sunflower and kelly green",
  fontsHref:
    "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,700;12..96,800&family=DM+Sans:wght@400;500;700&display=swap",
  isDark: false,
  navOnDark: false,
  heroLayout: "sticker",
  sectionAlign: "left",
  useFigureLabels: false,
  font: { display: '"Bricolage Grotesque", sans-serif', body: '"DM Sans", sans-serif', mono: '"DM Sans", sans-serif' },
  heading: { weight: 800, letterSpacing: "-0.03em", sizeScale: 1.05, transform: "none" },
  color: {
    pageBg: "#F6FFEA",
    surface: "#FFFFFF",
    surfaceAlt: "#FFF6C2",
    heading: "#101410",
    text: "#1E261F",
    muted: "#5C665E",
    accent: "#1F8A47",
    accentHover: "#176B37",
    accentDark: "#0F5A2C",
    accentSoft: "#D9F7E1",
    highlight: "#FFD84D",
    success: "#0F5A2C",
    border: "#101410",
    inputBg: "#FFFFFF",
    inputText: "#1E261F",
    footerBg: "#101410",
    footerText: "#F6FFEA",
    footerLink: "#B9C9B9",
  },
  shape: { cardRadius: "14px", buttonRadius: "12px" },
  card: { border: "2px solid #101410", shadow: "4px 4px 0 #101410" },
  button: { primaryColor: "#101410", shadow: "4px 4px 0 #101410", border: "2px solid #101410", hoverTransform: "translate(-2px, -2px)" },
  chart: {
    jungle: "#1F8A47",
    competitors: LIGHT_THEME_COMPETITOR_GREYS,
    gridline: "#E4EBDD",
    baseline: "#101410",
    trackBg: "#EFF5E8",
  },
  footer: { showIllustration: true },
};

// Seed read: mirrored pairs and palindromes, the double "88".
// Symmetry: everything centered, images matted like exhibits with captions.
const GALLERY_THEME: LandingTheme = {
  id: "gallery",
  name: "Gallery",
  direction: "Centered and framed — serif display, matted exhibits with captions, moss on white",
  fontsHref:
    "https://fonts.googleapis.com/css2?family=Source+Serif+4:ital,opsz,wght@0,8..60,500;0,8..60,600;1,8..60,500&family=Inter:wght@400;500;600&display=swap",
  isDark: false,
  navOnDark: false,
  heroLayout: "gallery",
  sectionAlign: "center",
  useFigureLabels: false,
  font: { display: '"Source Serif 4", Georgia, serif', body: '"Inter", sans-serif', mono: '"Inter", sans-serif' },
  heading: { weight: 600, letterSpacing: "-0.015em", sizeScale: 1.1, transform: "none" },
  color: {
    pageBg: "#FAFBF8",
    surface: "#FFFFFF",
    surfaceAlt: "#F1F5F0",
    heading: "#1B211D",
    text: "#343B36",
    muted: "#7A837C",
    accent: "#3E6E4E",
    accentHover: "#2F5A3E",
    accentDark: "#1E3A29",
    accentSoft: "#E6EEE7",
    highlight: "#3E6E4E",
    success: "#1E3A29",
    border: "#1B211D",
    inputBg: "#F1F5F0",
    inputText: "#343B36",
    footerBg: "#FFFFFF",
    footerText: "#1B211D",
    footerLink: "#5D6860",
  },
  shape: { cardRadius: "2px", buttonRadius: "2px" },
  card: { border: "1px solid #1B211D", shadow: "none" },
  button: { primaryColor: "#FFFFFF", shadow: "none", border: "none", hoverTransform: "none" },
  chart: {
    jungle: "#3E6E4E",
    competitors: LIGHT_THEME_COMPETITOR_GREYS,
    gridline: "#E6EAE6",
    baseline: "#1B211D",
    trackBg: "#F1F5F0",
  },
  footer: { showIllustration: false },
};

// Seed read: the repeating "RPRPR", "xx", "44", and "Dato".
// Measurement and rhythm: 44px grid paper, mono annotations, dimension lines.
const BLUEPRINT_THEME: LandingTheme = {
  id: "blueprint",
  name: "Blueprint",
  direction: "Engineering drawing — 44px grid paper, mono annotations, dimension lines, one orange stamp",
  fontsHref:
    "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap",
  isDark: false,
  navOnDark: false,
  heroLayout: "blueprint",
  sectionAlign: "left",
  useFigureLabels: true,
  font: { display: '"Space Grotesk", sans-serif', body: '"IBM Plex Sans", sans-serif', mono: '"IBM Plex Mono", monospace' },
  heading: { weight: 700, letterSpacing: "-0.02em", sizeScale: 1, transform: "none" },
  color: {
    pageBg: "#F4F8F4",
    surface: "#FFFFFF",
    surfaceAlt: "#EAF2EC",
    heading: "#0E3B26",
    text: "#21362B",
    muted: "#6A7D70",
    accent: "#0E7A44",
    accentHover: "#0A5C33",
    accentDark: "#0E3B26",
    accentSoft: "#DCEFE2",
    highlight: "#E8541E",
    success: "#0E3B26",
    border: "#0E3B26",
    inputBg: "#FFFFFF",
    inputText: "#21362B",
    footerBg: "#0E3B26",
    footerText: "#EAF2EC",
    footerLink: "#9DBFAB",
  },
  shape: { cardRadius: "4px", buttonRadius: "6px" },
  card: { border: "1px solid #0E3B26", shadow: "none" },
  button: { primaryColor: "#FFFFFF", shadow: "none", border: "none", hoverTransform: "none" },
  chart: {
    jungle: "#E8541E",
    competitors: LIGHT_THEME_COMPETITOR_GREYS,
    gridline: "#D6E3D9",
    baseline: "#0E3B26",
    trackBg: "#EAF2EC",
  },
  pageBackgroundLayer: {
    backgroundImage: "linear-gradient(#EAF0EB 1px, transparent 1px), linear-gradient(90deg, #EAF0EB 1px, transparent 1px)",
    backgroundSize: "44px 44px",
  },
  footer: { showIllustration: false },
};

// Moss: soft, tactile and cushioned — the forest floor rather than the canopy.
const MOSS_THEME: LandingTheme = {
  id: "moss",
  name: "Moss",
  direction: "The forest floor — moss photograph, moving canopy light, ferns in parallax, a grove that grows",
  isDark: false,
  navOnDark: true,
  heroLayout: "moss",
  sectionAlign: "center",
  useFigureLabels: false,
  usesOriginalSectionLayouts: true,
  font: { display: '"Gabarito", "Gabarito Fallback", sans-serif', body: '"Nunito Sans", sans-serif', mono: '"Nunito Sans", sans-serif' },
  heading: { weight: 700, letterSpacing: "-0.02em", sizeScale: 1.05, transform: "none" },
  color: {
    pageBg: "#EDF2E6",
    surface: "#F8FAF4",
    surfaceAlt: "#DCE8CF",
    heading: "#1F3A17",
    text: "#33482B",
    muted: "#596D50",
    accent: "#4E7923",
    accentHover: "#4A7422",
    accentDark: "#2C4A1C",
    accentSoft: "#DCE8CF",
    highlight: "#B7D86B",
    success: "#2C4A1C",
    border: "#C8D8B8",
    inputBg: "#F8FAF4",
    inputText: "#33482B",
    footerBg: "#1F3A17",
    footerText: "#EDF2E6",
    footerLink: "#A9C293",
  },
  shape: { cardRadius: "28px", buttonRadius: "999px" },
  card: { border: "1px solid #C8D8B8", shadow: "0 12px 32px rgba(44, 74, 28, 0.10)" },
  button: { primaryColor: "#FFFFFF", shadow: "none", border: "none", hoverTransform: "translateY(-2px)" },
  chart: {
    jungle: "#4E7923",
    competitors: LIGHT_THEME_COMPETITOR_GREYS,
    gridline: "#D9E4CD",
    baseline: "#9AAF8C",
    trackBg: "#E4ECDA",
  },
  footer: { showIllustration: true },
};

// The Moss family: same page, same copy and assets, a light hero instead of the forest-floor photograph.
// Each one is a different take on "jungle" with a light base.
const CLEARING_THEME: LandingTheme = {
  ...MOSS_THEME,
  id: "clearing",
  name: "Clearing",
  direction: "Moss on a light base — the fauna reaches in from both sides, dark green headline",
  navOnDark: false,
  heroLayout: "clearing",
};

const CANOPY_THEME: LandingTheme = {
  ...MOSS_THEME,
  id: "canopy",
  name: "Canopy",
  direction: "Standing under the canopy — leaf shadows and moving sunlight cast across a pale wall",
  navOnDark: false,
  heroLayout: "canopy",
};

const RIVERBANK_THEME: LandingTheme = {
  ...MOSS_THEME,
  id: "riverbank",
  name: "Riverbank",
  direction: "A pale sky over the animated jungle river — the app's own Rive scene as the horizon",
  navOnDark: false,
  heroLayout: "riverbank",
};

const DIORAMA_THEME: LandingTheme = {
  ...MOSS_THEME,
  id: "diorama",
  name: "Diorama",
  direction: "A paper diorama — layered leaves, hills and sun on separate depths that shift with the pointer",
  navOnDark: false,
  heroLayout: "diorama",
};

export const LANDING_THEMES: LandingTheme[] = [
  ORIGINAL_THEME,
  FIELD_GUIDE_THEME,
  NIGHT_LIBRARY_THEME,
  STICKER_THEME,
  GALLERY_THEME,
  BLUEPRINT_THEME,
  MOSS_THEME,
  CLEARING_THEME,
  CANOPY_THEME,
  RIVERBANK_THEME,
  DIORAMA_THEME,
];

export const DEFAULT_LANDING_THEME = ORIGINAL_THEME;

export function findLandingThemeById(themeId: string | null): LandingTheme {
  return LANDING_THEMES.find((theme) => theme.id === themeId) ?? DEFAULT_LANDING_THEME;
}
